import React, { createContext, useCallback, useContext, useState } from "react";

/**
 * TenantContext — Iter 32.
 *
 * Holds the company that should drive the tenant label on the NavBar.
 *
 *   - For regular users / hr_managers: NavBar derives the tenant label from
 *     the authenticated user (`user.company_name`). The override stays null.
 *
 *   - For super_admins: when they scope the Admin dashboard to a specific
 *     company (`activeCompanyId`), Admin.jsx pushes the resolved company
 *     object here so the NavBar reflects which tenant is currently being
 *     managed. When scope is reset to "all companies", the override is
 *     cleared and the NavBar falls back to the platform-only label.
 */
const TenantContext = createContext(null);

export const TenantProvider = ({ children }) => {
  const [override, setOverride] = useState(() => {
    try {
      const stored = localStorage.getItem("colygon_tenant_override");
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  const setTenantOverride = useCallback((company) => {
    setOverride(company || null);
    if (company) {
      localStorage.setItem("colygon_tenant_override", JSON.stringify(company));
    } else {
      localStorage.removeItem("colygon_tenant_override");
    }
  }, []);

  return (
    <TenantContext.Provider value={{ tenantOverride: override, setTenantOverride }}>
      {children}
    </TenantContext.Provider>
  );
};

export const useTenant = () => {
  const ctx = useContext(TenantContext);
  if (!ctx) {
    // Soft fallback so components outside the provider don't crash; equivalent
    // to "no override active".
    return { tenantOverride: null, setTenantOverride: () => {} };
  }
  return ctx;
};
